import { useSyncExternalStore } from "react";

type Size = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

type SizeProp = Size | `<${Size}`;

type MediaQuery = SizeProp | `(${string})`;

const breakpoints = new Map<SizeProp, number>([
  ["2xs", 360],
  ["xs", 425],
  ["sm", 576],
  ["md", 768],
  ["lg", 992],
  ["xl", 1200],
  ["2xl", 1440],
  ["3xl", 1920],
]);

export const useMedia = (query: MediaQuery) => {
  const store = getMediaQueryExternalStore(query);

  return useSyncExternalStore(store.subscribe, store.getSnapshot);
};

export const useTouch = () => {
  return useMedia("(pointer: coarse)");
};

const breakpointsMap: Map<string, string> = Array.from(
  breakpoints.entries(),
).reduce((map, [key, value]) => {
  map.set(`<${key}`, `(max-width: ${value - 0.02}px)`);
  map.set(key, `(min-width: ${value}px)`);

  return map;
}, new Map());

export const createQueryString = (query: MediaQuery) => {
  return breakpointsMap.get(query) ?? query;
};

export const createMediaQuery = (query: MediaQuery) => {
  return matchMedia(createQueryString(query));
};

interface MediaQueryListExternalStore {
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => boolean;
}

const mediaQueryListExternalStore = new Map<
  string,
  MediaQueryListExternalStore
>();

const getMediaQueryExternalStore = (query: MediaQuery) => {
  const key = createQueryString(query);

  return (
    mediaQueryListExternalStore.get(key) ?? createMediaQueryExternalStore(key)
  );
};

const createMediaQueryExternalStore = (
  query: string,
): MediaQueryListExternalStore => {
  const mediaQueryList = window.matchMedia(query);
  const listeners = new Set<() => void>();

  const onChange = () => {
    listeners.forEach((listener) => listener());
  };

  const store: MediaQueryListExternalStore = {
    subscribe: (onStoreChange) => {
      listeners.add(onStoreChange);

      // This will assign only one callback for `change` event if we have at leas one subscriber
      if (listeners.size === 1) {
        mediaQueryList.addEventListener("change", onChange);
      }

      return () => {
        listeners.delete(onStoreChange);

        // Remove listening for `change` event if there are no subscribers
        if (listeners.size === 0) {
          mediaQueryList.removeEventListener("change", onChange);
        }
      };
    },
    getSnapshot: () => mediaQueryList.matches,
  };

  mediaQueryListExternalStore.set(query, store);

  return store;
};
